import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import useAiJob from "@/hooks/useAiJob";
import { MissingTermsCard } from "@/components/admin/MissingTermsCard";
import { MagnifyingGlass, Plus, Trash, PencilSimple, Sparkle, UploadSimple, DownloadSimple, Image, TreeStructure } from "@phosphor-icons/react";

const CATEGORIES = ["Ingredient", "Cut of meat", "Technique", "Tool", "Abbreviation", "Measure", "Other"];

const EMPTY_FORM = {
  term_id: null,
  term: "",
  category: "Ingredient",
  definition: "",
  also_known_as: "",
  related_terms: "",
  seo_title: "",
  seo_description: "",
};

export default function KitchenHelpPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [genCount, setGenCount] = useState(50);
  const [genFocus, setGenFocus] = useState("");
  const [generating, setGenerating] = useState(false);
  const [csvReplace, setCsvReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [heroUrl, setHeroUrl] = useState("");
  const [uploadingHero, setUploadingHero] = useState(false);
  const [relatedMode, setRelatedMode] = useState("missing");
  const [suggestingRelated, setSuggestingRelated] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/kitchen-help")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
    api.get("/settings").then((r) => setHeroUrl(r.data?.kitchen_help_hero_url || "")).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((t) => {
      if (cat && t.category !== cat) return false;
      if (!query) return true;
      const hay = [t.term, t.definition, ...(t.also_known_as || []), ...(t.related_terms || [])].join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [items, q, cat]);

  const openNew = () => { setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (t) => {
    setForm({
      term_id: t.term_id,
      term: t.term,
      category: t.category,
      definition: t.definition,
      also_known_as: (t.also_known_as || []).join(", "),
      related_terms: (t.related_terms || []).join(", "),
      seo_title: t.seo_title || "",
      seo_description: t.seo_description || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.term.trim() || !form.definition.trim()) {
      toast.error("Term and definition are required");
      return;
    }
    const body = {
      term: form.term.trim(),
      category: form.category,
      definition: form.definition.trim(),
      also_known_as: form.also_known_as.split(",").map((s) => s.trim()).filter(Boolean),
      related_terms: form.related_terms.split(",").map((s) => s.trim()).filter(Boolean),
      seo_title: (form.seo_title || "").trim(),
      seo_description: (form.seo_description || "").trim(),
    };
    try {
      if (form.term_id) {
        await api.patch(`/admin/kitchen-help/${form.term_id}`, body);
        toast.success("Term updated");
      } else {
        await api.post("/admin/kitchen-help", body);
        toast.success("Term added");
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.term}"?`)) return;
    try {
      await api.delete(`/admin/kitchen-help/${t.term_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const generateJob = useAiJob({
    statusUrl: (id) => `/admin/kitchen-help/generate/status/${id}`,
    onDone: (job) => {
      // Sync path returns `inserted` + `skipped_duplicates`; async path returns
      // `inserted` + `skipped` on the job doc. Support both shapes.
      const inserted = job.inserted ?? 0;
      const skipped = job.skipped_duplicates ?? job.skipped ?? 0;
      toast.success(`AI added ${inserted} new terms (${skipped} duplicates skipped)`);
      setGenerating(false);
      load();
    },
    onCancelled: (job) => {
      toast(`Cancelled — kept ${job.inserted ?? 0} terms already inserted, dropped remaining ${(job.total ?? 0) - (job.processed ?? 0)} batch${((job.total ?? 0) - (job.processed ?? 0)) === 1 ? "" : "es"}.`);
      setGenerating(false);
      load();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
      setGenerating(false);
    },
  });

  const runAiGenerate = async () => {
    setGenerating(true);
    try {
      await generateJob.start(async () => {
        const r = await api.post("/admin/kitchen-help/generate", {
          count: Math.min(Math.max(parseInt(genCount, 10) || 50, 1), 100),
          focus: genFocus.trim() || undefined,
        });
        return r.data;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
      setGenerating(false);
    }
  };

  const runSuggestRelated = async () => {
    if (!window.confirm(`Suggest related terms for ${relatedMode === "all" ? "EVERY" : "entries WITHOUT any related terms yet"}? This uses Claude Sonnet 4.6.`)) return;
    setSuggestingRelated(true);
    try {
      const r = await api.post("/admin/kitchen-help/suggest-related", { mode: relatedMode });
      toast.success(`AI linked ${r.data.updated} of ${r.data.targeted} terms (${r.data.batches} batches)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggest failed");
    } finally {
      setSuggestingRelated(false);
    }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.url || "";
      await api.patch("/settings", { kitchen_help_hero_url: url });
      setHeroUrl(url);
      toast.success("Kitchen Help hero image updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingHero(false);
      e.target.value = "";
    }
  };

  const clearHero = async () => {
    if (!window.confirm("Remove the Kitchen Help hero image?")) return;
    try {
      await api.patch("/settings", { kitchen_help_hero_url: "" });
      setHeroUrl("");
      toast.success("Hero image removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("replace_existing", csvReplace ? "true" : "false");
      const r = await api.post("/admin/kitchen-help/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { inserted, updated, skipped, errors = [] } = r.data;
      toast.success(`CSV: ${inserted} added, ${updated} updated, ${skipped} skipped`);
      if (errors.length) toast.warning(`${errors.length} row error(s) — see console`);
      if (errors.length) console.warn("Kitchen Help CSV errors:", errors);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const exportCsv = () => {
    const header = ["term", "category", "definition", "also_known_as", "related_terms"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = items.map((t) => [
      t.term,
      t.category,
      t.definition,
      (t.also_known_as || []).join(" | "),
      (t.related_terms || []).join(" | "),
    ]);
    const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kitchen-help-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-kitchen-help-panel">
      {/* Header + Actions */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="font-display text-2xl font-black text-rf-ink">Kitchen Help glossary</h2>
          <p className="text-sm text-rf-ink-soft">{items.length} terms total · UK English culinary dictionary.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={openNew} data-testid="kh-add-btn" className="inline-flex items-center gap-2 bg-rf-orange text-white px-4 py-2 rounded-full font-bold hover:bg-rf-ink text-sm">
            <Plus size={16} weight="bold" /> Add term
          </button>
          <button onClick={exportCsv} data-testid="kh-export-csv" className="inline-flex items-center gap-2 bg-white border border-rf-ink/10 text-rf-ink px-4 py-2 rounded-full font-bold hover:bg-rf-ink hover:text-white text-sm">
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
        </div>
      </div>

      <MissingTermsCard
        refreshKey={items.length}
        onChanged={load}
        onEdit={(t) => openEdit(t)}
        onAddManual={(word) => { setForm({ ...EMPTY_FORM, term: word }); setShowForm(true); }}
      />

      {/* AI generator */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 p-5" data-testid="kh-ai-generator">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={20} weight="duotone" className="text-rf-orange" />
          <h3 className="font-bold text-rf-ink">Generate more terms with AI (Claude Sonnet 4.6)</h3>
        </div>
        <p className="text-sm text-rf-ink-soft mb-4">
          Adds new UK-English culinary entries in a single batch. Duplicates are automatically detected and skipped.
        </p>
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Count (1-100)</label>
            <input
              type="number"
              min={1}
              max={100}
              value={genCount}
              onChange={(e) => setGenCount(e.target.value)}
              data-testid="kh-ai-count"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Focus (optional)</label>
            <input
              value={genFocus}
              onChange={(e) => setGenFocus(e.target.value)}
              placeholder="e.g. baking, knife skills, Indian cuisine, offal cuts"
              data-testid="kh-ai-focus"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none"
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <button
              onClick={runAiGenerate}
              disabled={generating}
              data-testid="kh-ai-generate-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-rf-orange text-white px-4 py-2 rounded-full font-bold hover:bg-rf-ink text-sm disabled:opacity-50"
            >
              <Sparkle size={16} weight="bold" /> {generating ? "Generating…" : "Generate"}
            </button>
          </div>
        </div>
        {generateJob.progress && generateJob.progress.total > 0 && (
          <div className="mt-4" data-testid="kh-ai-progress">
            <div className="flex items-center justify-between text-xs font-bold text-rf-ink-soft mb-1">
              <span>
                {generateJob.progress.status === "done"
                  ? "Done"
                  : generateJob.progress.status === "cancelled"
                  ? "Cancelled"
                  : `Batch ${generateJob.progress.processed} / ${generateJob.progress.total} — ${generateJob.progress.inserted} inserted, ${generateJob.progress.skipped} skipped`}
              </span>
              <div className="flex items-center gap-3">
                <span>
                  {Math.round(((generateJob.progress.processed || 0) / (generateJob.progress.total || 1)) * 100)}%
                </span>
                {generateJob.progress.status === "running" && !generateJob.progress.cancel_requested && (
                  <button
                    type="button"
                    onClick={generateJob.cancel}
                    data-testid="kh-ai-cancel-btn"
                    className="text-rf-ink-soft hover:text-red-600 underline font-semibold"
                  >
                    Cancel
                  </button>
                )}
                {generateJob.progress.cancel_requested && generateJob.progress.status === "running" && (
                  <span className="text-amber-600 font-semibold">Cancelling…</span>
                )}
              </div>
            </div>
            <div className="w-full h-2 rounded-full bg-rf-ink/10 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  generateJob.progress.status === "cancelled" ? "bg-rf-ink-soft" : "bg-rf-orange"
                }`}
                style={{ width: `${Math.min(100, Math.round(((generateJob.progress.processed || 0) / (generateJob.progress.total || 1)) * 100))}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Suggest Related — AI-links every entry to a handful of related glossary terms */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 p-5" data-testid="kh-suggest-related">
        <div className="flex items-center gap-2 mb-3">
          <TreeStructure size={20} weight="duotone" className="text-rf-orange" />
          <h3 className="font-bold text-rf-ink">Weave the glossary — AI-suggest related terms</h3>
        </div>
        <p className="text-sm text-rf-ink-soft mb-4">
          Claude Sonnet 4.6 picks 3-5 genuinely related terms from your existing glossary and adds them
          to each entry&apos;s <code className="bg-rf-ink/5 px-1 py-0.5 rounded text-xs">related_terms</code> list.
          Existing admin-authored entries are preserved.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Mode</label>
            <select
              value={relatedMode}
              onChange={(e) => setRelatedMode(e.target.value)}
              data-testid="kh-related-mode"
              className="block mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none"
            >
              <option value="missing">Only entries with no related terms</option>
              <option value="all">Every entry (top up existing lists)</option>
            </select>
          </div>
          <button
            onClick={runSuggestRelated}
            disabled={suggestingRelated}
            data-testid="kh-suggest-related-btn"
            className="inline-flex items-center gap-2 bg-rf-orange text-white px-5 py-2 rounded-full font-bold hover:bg-rf-ink text-sm disabled:opacity-50"
          >
            <TreeStructure size={16} weight="bold" /> {suggestingRelated ? "Weaving…" : "Suggest related terms"}
          </button>
        </div>
      </div>

      {/* Hero image uploader */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 p-5" data-testid="kh-hero-uploader">
        <div className="flex items-center gap-2 mb-3">
          <Image size={20} weight="duotone" className="text-rf-orange" />
          <h3 className="font-bold text-rf-ink">Kitchen Help hero image</h3>
        </div>
        <p className="text-sm text-rf-ink-soft mb-4">
          Shown on the right of the hero on <code className="bg-rf-ink/5 px-1 py-0.5 rounded text-xs">/kitchen-help</code>{" "}
          — sits alongside the title (never behind it), matching the layout on <code className="bg-rf-ink/5 px-1 py-0.5 rounded text-xs">/historical</code>.
          Recommended: <strong>3:2 aspect ratio</strong> (e.g. 1500×1000) warm British kitchen scene. JPG / PNG / WebP, up to 10 MB.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          {heroUrl ? (
            <img src={heroUrl} alt="Kitchen Help hero" className="w-48 aspect-[3/2] object-cover rounded-lg border border-rf-ink/10" data-testid="kh-hero-preview" />
          ) : (
            <div className="w-48 aspect-[3/2] rounded-lg border-2 border-dashed border-rf-ink/20 flex items-center justify-center text-rf-ink/30 text-xs">
              No hero yet
            </div>
          )}
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={uploadHero}
              disabled={uploadingHero}
              data-testid="kh-hero-file"
              className="text-sm"
            />
          </label>
          {heroUrl && (
            <button onClick={clearHero} data-testid="kh-hero-remove" className="text-sm text-red-500 hover:text-red-700 font-bold">
              Remove
            </button>
          )}
          {uploadingHero && <span className="text-sm text-rf-orange">Uploading…</span>}
        </div>
      </div>

      {/* CSV import */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 p-5" data-testid="kh-csv-import">
        <div className="flex items-center gap-2 mb-3">
          <UploadSimple size={20} weight="duotone" className="text-rf-orange" />
          <h3 className="font-bold text-rf-ink">Bulk import from CSV</h3>
        </div>
        <p className="text-sm text-rf-ink-soft mb-3">
          Columns: <code className="bg-rf-ink/5 px-1 py-0.5 rounded text-xs">term, category, definition, also_known_as, related_terms</code>.
          Multi-values separated by <code>|</code> or <code>;</code>. Duplicates are skipped by default.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={importCsv}
              disabled={importing}
              data-testid="kh-csv-file"
              className="text-sm"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-rf-ink-soft">
            <input
              type="checkbox"
              checked={csvReplace}
              onChange={(e) => setCsvReplace(e.target.checked)}
              data-testid="kh-csv-replace"
            />
            Overwrite existing terms (by name)
          </label>
          {importing && <span className="text-sm text-rf-orange">Importing…</span>}
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-rf-ink/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search term, definition or aliases…"
            data-testid="kh-admin-search"
            className="w-full pl-10 pr-4 py-2 rounded-full border border-rf-ink/10 focus:border-rf-orange outline-none"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          data-testid="kh-admin-cat"
          className="px-4 py-2 rounded-full border border-rf-ink/10 focus:border-rf-orange outline-none"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-rf-orange p-5 space-y-3" data-testid="kh-form">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-rf-ink">{form.term_id ? "Edit term" : "Add new term"}</h3>
            <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="text-rf-ink-soft hover:text-rf-ink text-sm">Cancel</button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Term</label>
              <input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} data-testid="kh-form-term" className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="kh-form-category" className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Definition</label>
            <textarea rows={3} value={form.definition} onChange={(e) => setForm({ ...form, definition: e.target.value })} data-testid="kh-form-definition" className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none" />
            <p className="text-[11px] text-rf-ink-soft mt-1 leading-snug">
              Tip: cross-link related pages with{" "}
              <code className="font-mono bg-rf-ink/5 px-1 rounded">[courgette](/kitchen-help/courgette)</code>{" "}
              or <code className="font-mono bg-rf-ink/5 px-1 rounded">[our roast dinner guide](/news/perfect-sunday-roast)</code>{" "}
              — internal links (starting with <code>/</code>) open in the same tab, full URLs open in a new tab. Boosts SEO by weaving the glossary into the wider site.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Also known as (comma-separated)</label>
              <input value={form.also_known_as} onChange={(e) => setForm({ ...form, also_known_as: e.target.value })} data-testid="kh-form-aka" placeholder="Zucchini (US), Rucola" className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Related terms (comma-separated)</label>
              <input value={form.related_terms} onChange={(e) => setForm({ ...form, related_terms: e.target.value })} data-testid="kh-form-related" placeholder="Aubergine, Ratatouille" className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none" />
            </div>
          </div>
          {/* Per-term SEO overrides — save straight to the DB alongside the
              definition so admins can refine each entry's Google snippet
              without leaving this panel. Leave blank to fall back to
              auto-generated meta (term + definition). */}
          <div className="pt-3 border-t border-rf-ink/10">
            <p className="text-[11px] uppercase tracking-widest font-black text-rf-orange mb-2">SEO overrides (optional)</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Meta title</label>
                <input
                  value={form.seo_title}
                  onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                  maxLength={110}
                  data-testid="kh-form-seo-title"
                  placeholder={`${form.term || "Term"} — meaning, uses & substitutions | Recipe Filer`}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none"
                />
                <p className="text-[10px] text-rf-ink-soft mt-1">{(form.seo_title || "").length} / 110 characters — appears as the Google result title.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-rf-ink-soft">Meta description</label>
                <textarea
                  rows={2}
                  value={form.seo_description}
                  onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                  maxLength={200}
                  data-testid="kh-form-seo-description"
                  placeholder="Short, natural sentence that answers what the term means and when to use it — shown under the title in Google."
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-rf-ink/10 focus:border-rf-orange outline-none"
                />
                <p className="text-[10px] text-rf-ink-soft mt-1">{(form.seo_description || "").length} / 200 characters.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={save} data-testid="kh-form-save" className="bg-rf-orange text-white px-6 py-2 rounded-full font-bold hover:bg-rf-ink">
              {form.term_id ? "Save changes" : "Add term"}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-rf-ink/10 bg-rf-ink/5 text-[11px] uppercase tracking-wider font-bold text-rf-ink-soft">
          <div className="col-span-3">Term</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-5">Definition</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <p className="p-6 text-center text-rf-ink-soft">Loading…</p>}
        {!loading && filtered.length === 0 && <p className="p-6 text-center text-rf-ink-soft">No terms match.</p>}
        {!loading && filtered.slice(0, 500).map((t) => (
          <div key={t.term_id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-rf-ink/5 items-start hover:bg-rf-amber/5" data-testid={`kh-row-${t.slug}`}>
            <div className="col-span-3">
              <p className="font-bold text-rf-ink">{t.term}</p>
              {t.also_known_as && t.also_known_as.length > 0 && (
                <p className="text-xs text-rf-ink-soft mt-1">a.k.a. {t.also_known_as.join(", ")}</p>
              )}
            </div>
            <div className="col-span-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rf-amber/15 text-rf-orange text-[10px] font-bold uppercase tracking-wider">{t.category}</span>
            </div>
            <div className="col-span-5 text-sm text-rf-ink-soft leading-snug">{t.definition}</div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => openEdit(t)} data-testid={`kh-edit-${t.slug}`} className="text-rf-ink-soft hover:text-rf-orange" title="Edit">
                <PencilSimple size={18} weight="bold" />
              </button>
              <button onClick={() => remove(t)} data-testid={`kh-delete-${t.slug}`} className="text-rf-ink-soft hover:text-red-500" title="Delete">
                <Trash size={18} weight="bold" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length > 500 && (
          <p className="p-3 text-center text-xs text-rf-ink-soft">Showing first 500 of {filtered.length}. Use search / filter to narrow down.</p>
        )}
      </div>
    </div>
  );
}
